import React, { useState, useEffect } from 'react';
import { X, Calendar, CreditCard, AlertCircle, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import Modal from '../ui/Modal';
import apiService from '../../services/api';
import { SubscriptionDetails, BillingHistory, SubscriptionPlan } from '../../types/subscription';
import toast from 'react-hot-toast';
import DataGrid from '../grid/DataGrid';

interface SubscriptionManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
}

const SubscriptionManagementModal: React.FC<SubscriptionManagementModalProps> = ({
  isOpen,
  onClose,
  companyId,
  companyName
}) => {
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'billing' | 'actions'>('details');

  // Form states
  const [showExtendTrial, setShowExtendTrial] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showComplimentary, setShowComplimentary] = useState(false);

  // Extend trial form
  const [extendDays, setExtendDays] = useState(7);
  const [extendReason, setExtendReason] = useState('');

  // Change plan form
  const [newPlanId, setNewPlanId] = useState('');
  const [isAnnual, setIsAnnual] = useState(false);

  // Cancel form
  const [cancelReason, setCancelReason] = useState('');
  const [cancelImmediately, setCancelImmediately] = useState(false);

  // Complimentary form
  const [complimentaryPlanId, setComplimentaryPlanId] = useState('');
  const [complimentaryEndDate, setComplimentaryEndDate] = useState('');
  const [complimentaryReason, setComplimentaryReason] = useState('');

  useEffect(() => {
    if (isOpen && companyId) {
      loadData();
    }
  }, [isOpen, companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subscriptionResponse, billingResponse, plansData] = await Promise.all([
        apiService.getCompanySubscription(companyId),
        apiService.getBillingHistory(companyId),
        apiService.getAvailablePlans()
      ]);

      if (subscriptionResponse.success) {
        setSubscription(subscriptionResponse.data);
        if (subscriptionResponse.data) {
          setNewPlanId(subscriptionResponse.data.planId);
          setIsAnnual(subscriptionResponse.data.isAnnual);
        }
      }

      if (billingResponse.success) {
        setBillingHistory(billingResponse.data || []);
      }

      setPlans(plansData || []);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!extendReason.trim()) {
      toast.error('Please provide a reason for extending the trial');
      return;
    }

    try {
      setActionLoading('extend-trial');
      const response = await apiService.extendTrial(companyId, extendDays, extendReason);
      
      if (response.success) {
        toast.success(`Trial extended by ${extendDays} days`);
        setShowExtendTrial(false);
        setExtendDays(7);
        setExtendReason('');
        await loadData();
      } else {
        toast.error(response.message || 'Failed to extend trial');
      }
    } catch (error) {
      console.error('Error extending trial:', error);
      toast.error('Failed to extend trial');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangePlan = async () => {
    if (!newPlanId) {
      toast.error('Please select a plan');
      return;
    }

    try {
      setActionLoading('change-plan');
      const response = await apiService.changeSubscriptionPlan(companyId, newPlanId, isAnnual);
      
      if (response.success) {
        toast.success('Subscription plan updated successfully');
        setShowChangePlan(false);
        await loadData();
      } else {
        toast.error(response.message || 'Failed to change plan');
      }
    } catch (error) {
      console.error('Error changing plan:', error);
      toast.error('Failed to change subscription plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    try {
      setActionLoading('cancel');
      const response = await apiService.cancelSubscription(companyId, cancelReason, cancelImmediately);
      
      if (response.success) {
        toast.success('Subscription cancelled successfully');
        setShowCancel(false);
        setCancelReason('');
        setCancelImmediately(false);
        await loadData();
      } else {
        toast.error(response.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantComplimentary = async () => {
    if (!complimentaryPlanId || !complimentaryReason.trim()) {
      toast.error('Please select a plan and provide a reason');
      return;
    }

    try {
      setActionLoading('complimentary');
      const response = await apiService.grantComplimentaryAccess(
        companyId,
        complimentaryPlanId,
        complimentaryEndDate || null,
        complimentaryReason
      );
      
      if (response.success) {
        toast.success('Complimentary access granted successfully');
        setShowComplimentary(false);
        setComplimentaryPlanId('');
        setComplimentaryEndDate('');
        setComplimentaryReason('');
        await loadData();
      } else {
        toast.error(response.message || 'Failed to grant complimentary access');
      }
    } catch (error) {
      console.error('Error granting complimentary access:', error);
      toast.error('Failed to grant complimentary access');
    } finally {
      setActionLoading(null);
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      'Trial': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'Active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'PastDue': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'Suspended': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const billingColumns = [
    {
      key: 'date',
      label: 'Date',
      render: (item: BillingHistory) => formatDate(item.date)
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (item: BillingHistory) => formatCurrency(item.amount)
    },
    {
      key: 'status',
      label: 'Status',
      render: (item: BillingHistory) => getStatusBadge(item.status)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item: BillingHistory) => (
        item.invoiceUrl ? (
          <a
            href={item.invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
          >
            <ExternalLink className="w-4 h-4" />
            View Invoice
          </a>
        ) : (
          <span className="text-gray-400">No invoice available</span>
        )
      )
    }
  ];

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Subscription Management - ${companyName}`} maxWidth="4xl">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Subscription Management - ${companyName}`} maxWidth="4xl">
      <div className="space-y-6">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8">
            {(['details', 'billing', 'actions'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            {subscription ? (
              <>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Plan
                    </label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{subscription.planName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <div>{getStatusBadge(subscription.status)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Billing Cycle
                    </label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {subscription.isAnnual ? 'Annual' : 'Monthly'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Amount
                    </label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {formatCurrency(subscription.amount)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date
                    </label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDate(subscription.startDate)}
                    </p>
                  </div>
                  {subscription.trialEndDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Trial End Date
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(subscription.trialEndDate)}
                      </p>
                    </div>
                  )}
                  {subscription.currentPeriodEnd && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Current Period End
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(subscription.currentPeriodEnd)}
                      </p>
                    </div>
                  )}
                  {subscription.isComplimentary && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Complimentary Access
                        </label>
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                          <CheckCircle className="w-4 h-4 inline text-green-500 mr-1" />
                          Yes
                        </p>
                      </div>
                      {subscription.complimentaryEndDate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Complimentary End Date
                          </label>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {formatDate(subscription.complimentaryEndDate)}
                          </p>
                        </div>
                      )}
                      {subscription.complimentaryReason && (
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Reason
                          </label>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {subscription.complimentaryReason}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {subscription.stripeCustomerId && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Stripe Customer ID:</strong> {subscription.stripeCustomerId}
                    </p>
                    {subscription.stripeSubscriptionId && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <strong>Stripe Subscription ID:</strong> {subscription.stripeSubscriptionId}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No subscription found for this company</p>
              </div>
            )}
          </div>
        )}

        {/* Billing History Tab */}
        {activeTab === 'billing' && (
          <div>
            {billingHistory.length > 0 ? (
              <DataGrid
                data={billingHistory}
                columns={billingColumns}
                keyField="invoiceId"
                compact
              />
            ) : (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No billing history available</p>
              </div>
            )}
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && (
          <div className="space-y-6">
            {/* Extend Trial */}
            {subscription?.trialEndDate && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Extend Trial</h3>
                  <button
                    onClick={() => setShowExtendTrial(!showExtendTrial)}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                  >
                    {showExtendTrial ? 'Cancel' : 'Extend Trial'}
                  </button>
                </div>
                {showExtendTrial && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Additional Days
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={extendDays}
                        onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={extendReason}
                        onChange={(e) => setExtendReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Enter reason for extending trial..."
                      />
                    </div>
                    <button
                      onClick={handleExtendTrial}
                      disabled={actionLoading === 'extend-trial'}
                      className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === 'extend-trial' ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Extending...
                        </span>
                      ) : (
                        'Extend Trial'
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Change Plan */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Change Plan</h3>
                <button
                  onClick={() => setShowChangePlan(!showChangePlan)}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  {showChangePlan ? 'Cancel' : 'Change Plan'}
                </button>
              </div>
              {showChangePlan && (
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Select Plan
                    </label>
                    <select
                      value={newPlanId}
                      onChange={(e) => setNewPlanId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">Select a plan</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - {formatCurrency(plan.monthlyPrice)}/month
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isAnnual"
                      checked={isAnnual}
                      onChange={(e) => setIsAnnual(e.target.checked)}
                      className="mr-2"
                    />
                    <label htmlFor="isAnnual" className="text-sm text-gray-700 dark:text-gray-300">
                      Annual billing (save 20%)
                    </label>
                  </div>
                  <button
                    onClick={handleChangePlan}
                    disabled={actionLoading === 'change-plan' || !newPlanId}
                    className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'change-plan' ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Updating...
                      </span>
                    ) : (
                      'Update Plan'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Cancel Subscription */}
            {subscription && subscription.status !== 'Cancelled' && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Cancel Subscription</h3>
                  <button
                    onClick={() => setShowCancel(!showCancel)}
                    className="text-sm text-red-600 hover:text-red-800 dark:text-red-400"
                  >
                    {showCancel ? 'Cancel' : 'Cancel Subscription'}
                  </button>
                </div>
                {showCancel && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Enter reason for cancellation..."
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="cancelImmediately"
                        checked={cancelImmediately}
                        onChange={(e) => setCancelImmediately(e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="cancelImmediately" className="text-sm text-gray-700 dark:text-gray-300">
                        Cancel immediately (vs. at period end)
                      </label>
                    </div>
                    <button
                      onClick={handleCancel}
                      disabled={actionLoading === 'cancel'}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === 'cancel' ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Cancelling...
                        </span>
                      ) : (
                        'Cancel Subscription'
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Grant Complimentary Access */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Grant Complimentary Access</h3>
                <button
                  onClick={() => setShowComplimentary(!showComplimentary)}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  {showComplimentary ? 'Cancel' : 'Grant Access'}
                </button>
              </div>
              {showComplimentary && (
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Select Plan
                    </label>
                    <select
                      value={complimentaryPlanId}
                      onChange={(e) => setComplimentaryPlanId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">Select a plan</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End Date (optional - leave blank for unlimited)
                    </label>
                    <input
                      type="date"
                      value={complimentaryEndDate}
                      onChange={(e) => setComplimentaryEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={complimentaryReason}
                      onChange={(e) => setComplimentaryReason(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter reason for complimentary access..."
                    />
                  </div>
                  <button
                    onClick={handleGrantComplimentary}
                    disabled={actionLoading === 'complimentary'}
                    className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'complimentary' ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Granting...
                      </span>
                    ) : (
                      'Grant Complimentary Access'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SubscriptionManagementModal;

